/**
 * Persists an incoming share payload before the user clicks Accept (IndexedDB survives refresh).
 * Cleared on Accept / Decline / explicit teardown.
 */

const DB_NAME = "npp-web-share-staged-v1";
const DB_VERSION = 1;
const STORE = "staged";
const KEY = "current";

export type StagedShareRecord = {
  fp: string;
  utf8: string;
  tabTitle: string | null;
  byteLength: number;
  savedAt: number;
};

function openDb(): Promise<IDBDatabase> {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("IndexedDB is not available in this environment."));
  }
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = (): void => reject(req.error ?? new Error("indexedDB.open failed"));
    req.onsuccess = (): void => resolve(req.result);
    req.onupgradeneeded = (): void => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
  });
}

export async function writeStagedShare(rec: StagedShareRecord): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = (): void => {
      db.close();
      resolve();
    };
    tx.onerror = (): void => {
      db.close();
      reject(tx.error ?? new Error("writeStagedShare transaction failed"));
    };
    tx.onabort = (): void => {
      db.close();
      reject(tx.error ?? new Error("writeStagedShare transaction aborted"));
    };
    tx.objectStore(STORE).put(rec, KEY);
  });
}

export async function readStagedShare(): Promise<StagedShareRecord | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    let result: StagedShareRecord | null = null;
    const req = tx.objectStore(STORE).get(KEY);
    req.onsuccess = (): void => {
      result = (req.result as StagedShareRecord | undefined) ?? null;
    };
    req.onerror = (): void => reject(req.error ?? new Error("readStagedShare get failed"));
    tx.oncomplete = (): void => {
      db.close();
      resolve(result);
    };
    tx.onerror = (): void => {
      db.close();
      reject(tx.error ?? new Error("readStagedShare transaction failed"));
    };
    tx.onabort = (): void => {
      db.close();
      reject(tx.error ?? new Error("readStagedShare transaction aborted"));
    };
  });
}

export async function clearStagedShare(): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = (): void => {
      db.close();
      resolve();
    };
    tx.onerror = (): void => {
      db.close();
      reject(tx.error ?? new Error("clearStagedShare transaction failed"));
    };
    tx.objectStore(STORE).delete(KEY);
  });
}
